// APIs
import address from './address'
import altParty from './altParty'
import analytics from './analytics'
import appointment from './appointment'
import appState from './appState'
import config from './config'
import customer from './customer'
import disburseMethod from './disburseMethod'
import documents from './documents'
import loan from './loan'
import offers from './offers'
import origination from './origination'
import payment from './payment'
import paymentMethod from './paymentMethod'
import referral from './referral'
import states from './states'
import storeApi from './store'
import user from './user'
import videos from './videos'

// Libraries
import axios from 'axios'

// Utilities
import isEmpty from '../utility/isEmpty'
import Logger from '../utility/loggerUtil'
import Session from '../utility/sessionUtil'

import { SettingsConstants } from '../constants'
import store from '../store'

export default {
  address,
  altParty,
  analytics,
  appointment,
  appState,
  config,
  customer,
  disburseMethod,
  documents,
  loan,
  offers,
  origination,
  payment,
  paymentMethod,
  referral,
  states,
  store: storeApi,
  user,
  videos
}

export const tmxApiCall = async (method, path, params) => {
  try {
    const response = await tmxApi[method](path, params)

    return response.data
  } catch (error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      Logger.debug(error.response.data)
      Logger.debug(error.response.status)
      Logger.debug(error.response.headers)
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      Logger.debug(error.request)
    } else {
      // Something happened in setting up the request that triggered an Error
      Logger.debug('Error', error.message)
    }

    throw error
  }
}

export const tmxApi = axios.create({
  baseURL: store && store.state && store.state.session && store.state.session.servicesConfig && store.state.session.servicesConfig.serviceUrl ? store.state.session.servicesConfig.serviceUrl : process.env.VUE_APP_TmxServicesUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json; charset=utf-8'
  },
  timeout: SettingsConstants.defaultApiTimeout // Just in case API Gateway is down
})

tmxApi.interceptors.request.use(
  async config => {
    config.baseURL = store && store.state && store.state.session && store.state.session.servicesConfig && store.state.session.servicesConfig.serviceUrl ? store.state.session.servicesConfig.serviceUrl : process.env.VUE_APP_TmxServicesUrl

    const token = Session.getAccessToken()
    const idToken = Session.getIdToken()

    if (token) {
      config.headers.Authorization = token
    }

    if (idToken) {
      config.headers['x-mobile-app-id-token'] = idToken
    }

    const deviceKey = await store.dispatch('session/getCurrentDeviceKey')
      .catch(e => {
        Logger.error('Error getting device key')
        Logger.debug(e)
      })

    if (deviceKey) {
      config.headers['x-mobile-app-device-key'] = deviceKey
    }

    config.headers['Cache-Control'] = 'no-cache' // This is needed to prevent aggressive IE caching of GET requests
    config.headers.Expires = '0' // This is needed to prevent aggressive IE caching behavior of GET requests
    config.headers.Pragma = 'no-cache' // This is needed to prevent aggressive IE caching behavior of GET requests

    config.headers['Accept-Language'] = window.xlang
    config.headers['x-api-version'] = process.env.VUE_APP_ApiVersion
    config.headers['x-mobile-app-version'] = SettingsConstants.appVersion
    config.headers['x-mobile-app-brand'] = process.env.VUE_APP_AppBrand
    config.headers['x-mobile-app-pwa'] = store.state.display ? (store.state.display.isPwa || 'false').toString() : 'false'
    return config
  },
  error => Promise.reject(error)
)

/**
 * Makes an API call to a non-self assessment endpoint (3rd party endpoint).
 * @param {Object} options - API call options.
 * @param {String} options.method - The API method used in request.
 * @param {Sting} options.url - The URL to use in request.
 * @param  {Object} [options.headers] - API headers for the request.
 * @param  {Any} [data] - Data to send in the request.
 * @returns {Promise<Object>} The API response, if any.
 */
export const axiosApiCall = async (options) => {
  const path = '/' + options.url.split('/').pop()

  try {
    logRequest(options.method, path, options)
    const axiosApi = axios.create()
    axiosApi.defaults.timeout = SettingsConstants.defaultApiTimeout
    const response = await axiosApi(options)

    logResponse(options.method, path, response.data)

    return response.data
  } catch (error) {
    handleError(path, options.method, error)

    throw error
  }
}

const logRequest = (method, path, data) => {
  const prefix = 'REQUEST:\n'
  Logger.debug(formatDetails(method, path, prefix + (isEmpty(data) ? 'empty' : JSON.stringify(data))))
}

const formatDetails = (method, path, content) => `${method.toUpperCase()}:${path} - ${content}`

const logResponse = (method, path, data) => {
  const prefix = 'RESPONSE:\n'

  const isFile = path.includes('svg')

  if (isEmpty(data)) {
    data = 'empty'
  } if (isFile) {
    data = data.substring(0, 5) + '...'
  } else {
    data = JSON.stringify(data)
  }

  Logger.debug(formatDetails(method, path, prefix + data))
}

const handleError = (method, path, error) => {
  Logger.error(formatDetails(method, path, 'Error response'))

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    Logger.debug(formatDetails(method, path, JSON.stringify(error.response.data)))
    Logger.debug(formatDetails(method, path, JSON.stringify(error.response.data)))
    Logger.debug(formatDetails(method, path, JSON.stringify(error.response.status)))
    Logger.debug(formatDetails(method, path, JSON.stringify(error.response.headers)))
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    Logger.debug(formatDetails(method, path, JSON.stringify(error.request)))
  } else {
    // Something happened in setting up the request that triggered an Error
    Logger.debug(formatDetails(method, path, JSON.stringify(error.message)))
  }
}
